export interface DesignTokens {
  readonly color: {
    readonly transparent: string
    readonly white: string
    readonly black: string
    readonly night: string
    readonly ink: string
    readonly charcoal: string
    readonly navy: string
    readonly navyDeep: string
    readonly slate: string
    readonly blue: string
    readonly sky: string
    readonly skyBright: string
    readonly ice: string
    readonly snow: string
    readonly cloud: string
    readonly frost: string
    readonly skyPale: string
    readonly violet: string
    readonly teal: string
    readonly lavender: string
    readonly magenta: string
    readonly red: string
    readonly success: string
    readonly danger: string
  }
  readonly semanticColor: {
    readonly canvas: string
    readonly surface: string
    readonly surfaceElevated: string
    readonly surfaceGlass: string
    readonly control: string
    readonly controlFocus: string
    readonly text: string
    readonly textMuted: string
    readonly textSubtle: string
    readonly fieldLabel: string
    readonly placeholder: string
    readonly accent: string
    readonly accentStrong: string
    readonly focus: string
    readonly border: string
    readonly borderStrong: string
    readonly success: string
    readonly danger: string
  }
  readonly space: {
    readonly none: string
    readonly px: string
    readonly xs: string
    readonly sm: string
    readonly md: string
    readonly lg: string
    readonly xl: string
    readonly "2xl": string
    readonly "3xl": string
    readonly "4xl": string
    readonly "5xl": string
  }
  readonly size: {
    readonly content: string
    readonly contentWide: string
    readonly controlSmall: string
    readonly control: string
    readonly controlLarge: string
  }
  readonly radius: {
    readonly none: string
    readonly sm: string
    readonly md: string
    readonly lg: string
    readonly xl: string
    readonly round: string
  }
  readonly font: {
    readonly body: string
    readonly mono: string
  }
  readonly fontSize: {
    readonly xs: string
    readonly sm: string
    readonly md: string
    readonly lg: string
    readonly xl: string
    readonly "2xl": string
    readonly "3xl": string
    readonly "4xl": string
  }
  readonly fontWeight: {
    readonly regular: number
    readonly medium: number
    readonly bold: number
    readonly black: number
  }
  readonly lineHeight: {
    readonly tight: number
    readonly heading: number
    readonly compact: number
    readonly body: number
  }
  readonly shadow: {
    readonly sm: string
    readonly md: string
    readonly lg: string
    readonly focus: string
  }
  readonly duration: {
    readonly instant: string
    readonly fast: string
    readonly normal: string
    readonly page: string
  }
  readonly easing: {
    readonly standard: string
    readonly entrance: string
  }
  readonly breakpoint: {
    readonly sm: string
    readonly md: string
    readonly lg: string
    readonly xl: string
    readonly wide: string
  }
  readonly zIndex: {
    readonly base: number
    readonly chrome: number
    readonly overlay: number
    readonly modal: number
  }
}

export const tokens: DesignTokens = {
  color: {
    transparent: "transparent",
    white: "#ffffff",
    black: "#000000",
    night: "#00020b",
    ink: "#050915",
    charcoal: "#0e0f11",
    navy: "#051a5c",
    navyDeep: "#03113c",
    slate: "#080c1c",
    blue: "#1c54a8",
    sky: "#59a4e1",
    skyBright: "#8bdcff",
    ice: "#9ad4f5",
    snow: "#f3ffff",
    cloud: "#f4f9ff",
    frost: "#eaf8ff",
    skyPale: "#d9f2ff",
    violet: "#708bff",
    teal: "#4bcab3",
    lavender: "#a489e5",
    magenta: "#9d316e",
    red: "#de2d3e",
    success: "#93f3ca",
    danger: "#ffb6ae",
  },
  semanticColor: {
    canvas: "#050915",
    surface: "#00020b",
    surfaceElevated: "#0e0f11",
    surfaceGlass: "rgba(9, 17, 38, 0.88)",
    control: "rgba(3, 8, 22, 0.72)",
    controlFocus: "rgba(3, 8, 22, 0.92)",
    text: "#f3ffff",
    textMuted: "rgba(226, 241, 255, 0.7)",
    textSubtle: "rgba(226, 241, 255, 0.72)",
    fieldLabel: "rgba(226, 241, 255, 0.86)",
    placeholder: "rgba(226, 241, 255, 0.42)",
    accent: "#8bdcff",
    accentStrong: "#59a4e1",
    focus: "#8bdcff",
    border: "rgba(139, 220, 255, 0.18)",
    borderStrong: "rgba(139, 220, 255, 0.42)",
    success: "#93f3ca",
    danger: "#ffb6ae",
  },
  space: {
    none: "0",
    px: "1px",
    xs: "0.25rem",
    sm: "0.5rem",
    md: "0.75rem",
    lg: "1rem",
    xl: "1.5rem",
    "2xl": "2rem",
    "3xl": "3rem",
    "4xl": "4rem",
    "5xl": "6rem",
  },
  size: {
    content: "72rem",
    contentWide: "86.25rem",
    controlSmall: "2.5rem",
    control: "3rem",
    controlLarge: "3.5rem",
  },
  radius: {
    none: "0",
    sm: "0.25rem",
    md: "0.5rem",
    lg: "0.75rem",
    xl: "2rem",
    round: "9999px",
  },
  font: {
    body: "Lato, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
    mono: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  },
  fontSize: {
    xs: "0.75rem",
    sm: "0.875rem",
    md: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "2rem",
    "4xl": "3rem",
  },
  fontWeight: {
    regular: 400,
    medium: 600,
    bold: 700,
    black: 900,
  },
  lineHeight: {
    tight: 1,
    heading: 1.1,
    compact: 1.4,
    body: 1.6,
  },
  shadow: {
    sm: "0 0.25rem 1rem rgba(0, 2, 11, 0.18)",
    md: "0 1rem 3rem rgba(0, 2, 11, 0.26)",
    lg: "0 3rem 7rem rgba(0, 0, 0, 0.34)",
    focus: "0 0 0 3px rgba(139, 220, 255, 0.24)",
  },
  duration: {
    instant: "0ms",
    fast: "120ms",
    normal: "180ms",
    page: "520ms",
  },
  easing: {
    standard: "ease",
    entrance: "cubic-bezier(0.22, 1, 0.36, 1)",
  },
  breakpoint: {
    sm: "30rem",
    md: "40rem",
    lg: "64rem",
    xl: "80rem",
    wide: "125rem",
  },
  zIndex: {
    base: 0,
    chrome: 80,
    overlay: 100,
    modal: 1000,
  },
}

export type SpaceToken = keyof DesignTokens["space"]
export type RadiusToken = keyof DesignTokens["radius"]
export type FontSizeToken = keyof DesignTokens["fontSize"]
